import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import ReactMarkdown from "react-markdown";
import Navbar from "../components/Navbar";
import SEO from "../components/SEO";
import api from "../lib/api";
import { Clock, ArrowLeft, ArrowRight, Tag } from "lucide-react";
import { toast } from "sonner";

const CATEGORY_COLOR = {
  Guide: "bg-nexus-purple/10 text-nexus-purple border-nexus-purple/30",
  Playbook: "bg-nexus-blue/10 text-nexus-blue border-nexus-blue/30",
  Explainer: "bg-emerald-500/10 text-emerald-300 border-emerald-500/30",
  "Creator Guide": "bg-nexus-pink/10 text-nexus-pink border-nexus-pink/30",
};

function catCls(c) {
  return CATEGORY_COLOR[c] || "bg-white/5 text-zinc-300 border-white/10";
}

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch { return ""; }
}

const MD_COMPONENTS = {
  h2: (props) => <h2 className="font-display font-bold text-2xl md:text-3xl tracking-tight mt-10 mb-4" {...props} />,
  h3: (props) => <h3 className="font-display font-bold text-xl tracking-tight mt-8 mb-3" {...props} />,
  p: (props) => <p className="text-zinc-200 leading-relaxed my-4" {...props} />,
  ul: ({ ordered, ...props }) => <ul className="list-disc pl-6 my-4 space-y-2 text-zinc-200 marker:text-nexus-purple" {...props} />,
  ol: ({ ordered, ...props }) => <ol className="list-decimal pl-6 my-4 space-y-2 text-zinc-200 marker:text-nexus-purple" {...props} />,
  li: ({ ordered, ...props }) => <li {...props} />,
  a: (props) => <a className="nexus-gradient-text font-semibold underline underline-offset-4 hover:opacity-80" {...props} />,
  strong: (props) => <strong className="text-white font-semibold" {...props} />,
  hr: () => <hr className="my-10 border-white/10" />,
  blockquote: (props) => <blockquote className="my-6 pl-4 border-l-2 border-nexus-purple text-zinc-300 italic" {...props} />,
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/blog/${slug}`),
      api.get("/blog"),
    ])
      .then(([p, all]) => {
        setPost(p.data);
        setRelated(all.data.filter((x) => x.slug !== slug).slice(0, 3));
      })
      .catch(() => toast.error("Article not found"))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen grain">
        <Navbar />
        <div className="max-w-3xl mx-auto p-10 text-zinc-500 animate-pulse">Loading…</div>
      </div>
    );
  }
  if (!post) return null;

  return (
    <div className="min-h-screen grain">
      <SEO
        title={post.title}
        description={post.description}
        path={`/blog/${post.slug}`}
        image={post.cover_image}
        type="article"
        keywords={[...(post.tags || []), ...(post.seo_keywords || [])]}
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Article",
          "headline": post.title,
          "description": post.description,
          "image": post.cover_image,
          "datePublished": post.published_at,
          "dateModified": post.published_at,
          "author": { "@type": "Organization", "name": post.author || "Nexus" },
          "publisher": {
            "@type": "Organization",
            "name": "Nexus",
            "logo": {
              "@type": "ImageObject",
              "url": "https://collabnexus.co.uk/nexus-logo.png",
            },
          },
          "mainEntityOfPage": `${window.location.origin}/blog/${post.slug}`,
          "keywords": (post.tags || []).join(", "),
          "articleSection": post.category,
        }}
      />
      <Navbar />

      <article className="max-w-3xl mx-auto px-6 lg:px-10 pt-10 pb-16">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white mb-8" data-testid="back-to-blog">
          <ArrowLeft className="h-4 w-4" /> Back to blog
        </Link>

        <div className="flex items-center gap-2 mb-4">
          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${catCls(post.category)}`}>
            {post.category}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {post.reading_minutes} min read
          </span>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500">
            · {formatDate(post.published_at)}
          </span>
        </div>

        <h1 className="font-display font-bold text-4xl md:text-5xl tracking-tighter leading-tight mb-6" data-testid="blog-post-title">
          {post.title}
        </h1>
        <p className="text-lg text-zinc-300 leading-relaxed mb-8">{post.description}</p>

        <div className="rounded-2xl overflow-hidden hairline mb-10">
          <img src={post.cover_image} alt={post.title} className="w-full h-auto object-cover" />
        </div>

        <div
          className="prose-nexus"
          data-testid="blog-post-content"
        >
          <ReactMarkdown components={MD_COMPONENTS}>{post.content}</ReactMarkdown>
        </div>

        {post.tags?.length > 0 && (
          <div className="mt-12 flex flex-wrap gap-2" data-testid="blog-tags">
            {post.tags.map((t) => (
              <span key={t} className="text-[10px] uppercase tracking-widest px-2.5 py-1 rounded-full border border-white/10 bg-white/[0.02] text-zinc-300 flex items-center gap-1">
                <Tag className="h-3 w-3" /> {t}
              </span>
            ))}
          </div>
        )}

        <div className="mt-14 glass rounded-2xl p-8 text-center">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-3">Ready to run one?</div>
          <div className="font-display font-bold text-2xl mb-4">Bring this playbook to Nexus.</div>
          <div className="flex gap-3 justify-center flex-wrap">
            <Link to="/discover" className="nexus-gradient text-white text-sm font-semibold px-5 h-11 rounded-full inline-flex items-center gap-2 hover:opacity-90">
              Browse creators <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to="/campaigns/new" className="bg-white/5 border border-white/15 text-white text-sm font-semibold px-5 h-11 rounded-full inline-flex items-center hover:bg-white/10">
              Post a campaign
            </Link>
          </div>
        </div>
      </article>

      {related.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-14 border-t border-white/5">
          <div className="text-xs uppercase tracking-[0.3em] text-zinc-500 mb-6">Read next</div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="blog-related">
            {related.map((p) => (
              <Link
                key={p.slug}
                to={`/blog/${p.slug}`}
                className="rounded-2xl overflow-hidden hairline bg-[#0C0C10] card-hover"
                data-testid={`blog-related-${p.slug}`}
              >
                <div className="aspect-[16/9] overflow-hidden">
                  <img src={p.cover_image} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <div className={`inline-block text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full border ${catCls(p.category)} mb-2`}>
                    {p.category}
                  </div>
                  <div className="font-display font-bold text-lg leading-tight">{p.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
